import { forwardRef, type ButtonHTMLAttributes } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '../../lib/cn';
import { BUTTON_BASE, BUTTON_VARIANTS, type ButtonVariant } from './Button';

export type IconButtonSize = 'xs' | 'sm' | 'md';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The one icon. lucide-react only. */
  icon: LucideIcon;
  /** REQUIRED — an icon-only control is invisible to screen readers without it. */
  'aria-label': string;
  size?: IconButtonSize;
  variant?: ButtonVariant;
}

// Square boxes on the control tokens. 24px is the WCAG 2.2 floor.
const BOX: Record<IconButtonSize, string> = {
  xs: 'h-[var(--size-control-xs)] w-[var(--size-control-xs)]', // 24px — icon-only in a dense row
  sm: 'h-[var(--size-control-sm)] w-[var(--size-control-sm)]', // 28px
  md: 'h-[var(--size-control-md)] w-[var(--size-control-md)]', // 32px — toolbar default
};

const ICON_PX: Record<IconButtonSize, number> = { xs: 12, sm: 16, md: 16 };

/** Square icon-only button — replaces every bare `<ChevronX>` click target.
 *  Focus is owned by the global :focus-visible rule. */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon: Icon, size = 'md', variant = 'ghost', className, title, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      title={title ?? rest['aria-label']}
      className={cn(BUTTON_BASE, BUTTON_VARIANTS[variant], BOX[size], 'p-0', className)}
      {...rest}
    >
      <Icon size={ICON_PX[size]} strokeWidth={2} aria-hidden="true" />
    </button>
  );
});

export default IconButton;
